import { useEffect, useState, useMemo, useCallback, useRef, memo } from "react";
import { Route, Routes, Navigate } from "react-router-dom";
import ErrorBoundary from "@/shared/components/ui/Error/ErrorBoundary";
import LayoutBg from "@/shared/components/layout/LayoutBg";
import { fetchCurrentUser } from "@/api/auth";
import Accounts from "@/features/accounts/components/Accounts";
import AccountDiscovery from "@/features/accounts/components/AccountDiscovery";
import ProfileDetailedView from "@/features/accounts/components/ProfileDetailedView";
import Campaigns from "@/features/campaigns/components/UserCampaigns/Campaigns";
import ChatApp from "@/features/chat/components/ChatApp";
import MainAppLayout from "@/shared/components/layout/MainAppLayout";
import { useMainStore } from "@/stores";
import Loader from "@/shared/components/ui/Loaders/Loader";
import Contacts from "Pages/Contacts";
import ContactDetailPage from "@/features/contacts/components/ContactDetailPage";
import NotFoundPage from "Pages/PageNotFound";
import DashboardPage from "Pages/DashboardPage";
import AiTrainer from "Pages/AiTrainer";
import Settings from "Pages/Settings";
import CampaignDetailsPage from "Pages/CampaignDetailsPage";
import SalesCoach from "Pages/SalesCoach";
import OrgAdminSalesCoach from "Pages/OrgAdminSalesCoach";
import GlobalEnrollmentBatchTracker from "@/features/campaigns/components/GlobalEnrollmentBatchTracker";
import ActiveCallBar from "@/features/voice/components/ActiveCallBar";
import Pricing from "Pages/Pricing";
import AdminRequest from "Pages/AdminRequest";
import JoinOrganization from "Pages/JoinOrganization";
import ConfirmationOrgJoin from "Pages/ConfirmationOrgJoin";
import AccessRequestStatus from "Pages/AccessRequestStatus";
import AccessRequestRejected from "Pages/AccessRequestRejected";
import SuperAdminDashboard from "Pages/SuperAdmin/SuperAdminDashboard";
import OrganizationsList from "Pages/SuperAdmin/OrganizationsList";
import OrganizationUsers from "Pages/SuperAdmin/OrganizationUsers";
import CallingApprovals from "Pages/SuperAdmin/CallingApprovals";
import OrgAdminDashboard from "Pages/OrgAdmin/OrgAdminDashboard";
import MembersList from "Pages/OrgAdmin/MembersList";
import PendingMembers from "Pages/OrgAdmin/PendingMembers";
import {
  getTargetRoute,
  mayAccessPostOnboardingPricing,
} from "@/shared/utils/helperFunctions";
import { USER_ROLES } from "@/shared/utils/constants";
import NewOnboardingNewUser from "@/features/onboarding/components/Onboarding/NewOnboardingNewUser";
import AiWorkspace from "Pages/AiWorkspace";
import Prospect from "@/Pages/Prospect";

// Route constants for maintainability
const ROUTES = {
  HOME: "/",
  ONBOARDING: "/onboarding",
  QUICK_SETUP: "/quick-setup",
  TAILORED_SETUP: "/tailored-setup",
  DASHBOARD: "/dashboard",
  CAMPAIGNS: "/campaigns",
  ACCOUNTS: "/accounts",
  ACCOUNT_DISCOVERY: "/accounts/find",
  PROSPECTING: "/prospecting",
  PROSPECT: "/prospect",
  AI_TRAINER: "/ai-trainer",
  AI_WORKSPACE: "/ai-workspace",
  CHAT: "/chat",
  CONTACTS: "/contacts",
  SALES_COACH: "/sales-coach",
  OA_SALES_COACH: "/oa/sales-coach",
  SELECTED_PROSPECTS: "/selected-prospects",
  SETTINGS: "/settings",
  PRICING: "/pricing",
  JOIN_ORGANIZATION: "/join-organization",
  JOIN_ORGANIZATION_CONFIRM: "/join-organization/confirm",
  JOIN_ORGANIZATION_STATUS: "/join-organization/status",
  JOIN_ORGANIZATION_REJECTED: "/join-organization/rejected",
  ADMIN_REQUEST: "/admin-request",
  SA_DASHBOARD: "/sa/dashboard",
  SA_ORGANIZATIONS: "/sa/organizations",
  SA_CALLING_APPROVALS: "/sa/calling-approvals",
  OA_DASHBOARD: "/oa/dashboard",
  OA_MEMBERS: "/oa/members",
  OA_PENDING_MEMBERS: "/oa/pending-members",
};

/** pricing: only show child when user belongs on this route. */
const RouteGuard = memo(({ user, expectedRoute, appMode, children }) => {
  const modeOpts = { appMode };
  if (expectedRoute === ROUTES.PRICING) {
    if (mayAccessPostOnboardingPricing(user, modeOpts)) return <>{children}</>;
    return <Navigate to={getTargetRoute(user, modeOpts)} replace />;
  }
  const target = getTargetRoute(user, modeOpts);
  if (target === expectedRoute) return <>{children}</>;
  return <Navigate to={target} replace />;
});
RouteGuard.displayName = "RouteGuard";

/** Join org / admin request: onboarded, no request submitted yet, not approved/rejected. */
const canAccessAccessRequestFlow = (user) =>
  Boolean(
    user?.isOnboarded &&
    !user?.hasAccessRequest &&
    user?.approvalStatus !== "approved" &&
    user?.approvalStatus !== "rejected",
  );

const JoinFlowGuard = memo(({ user, appMode, children }) => {
  const modeOpts = { appMode };
  if (canAccessAccessRequestFlow(user)) return <>{children}</>;
  return <Navigate to={getTargetRoute(user, modeOpts)} replace />;
});
JoinFlowGuard.displayName = "JoinFlowGuard";

const AccessStatusGuard = memo(({ user, appMode, children }) => {
  const modeOpts = { appMode };
  if (user?.hasAccessRequest && user?.approvalStatus === "pending") {
    return <>{children}</>;
  }
  return <Navigate to={getTargetRoute(user, modeOpts)} replace />;
});
AccessStatusGuard.displayName = "AccessStatusGuard";

const RejectedOnlyGuard = memo(({ user, appMode, children }) => {
  const modeOpts = { appMode };
  if (user?.approvalStatus === "rejected") return <>{children}</>;
  return <Navigate to={getTargetRoute(user, modeOpts)} replace />;
});
RejectedOnlyGuard.displayName = "RejectedOnlyGuard";

/* Protected Route — main app (campaigns, contacts, …) for approved standard users only */
const ProtectedRoute = memo(({ children, userHasProfile, user, appMode }) => {
  if (
    user?.role === USER_ROLES.SUPER_ADMIN ||
    user?.role === USER_ROLES.SUPER_ADMIN_ALT
  ) {
    return <Navigate to={ROUTES.SA_DASHBOARD} replace />;
  }
  if (user?.role === USER_ROLES.ORG_ADMIN && appMode !== "user") {
    return <Navigate to={getTargetRoute(user, { appMode })} replace />;
  }
  if (!userHasProfile || !user?.isOnboarded) {
    return <Navigate to={ROUTES.ONBOARDING} replace />;
  }
  const target = getTargetRoute(user, { appMode });
  if (target === ROUTES.CAMPAIGNS) {
    return <>{children}</>;
  }
  return <Navigate to={target} replace />;
});
ProtectedRoute.displayName = "ProtectedRoute";

/** Org admin console routes: hidden when acting as member (user mode). */
const OrgAdminAdminModeOnly = memo(({ children }) => {
  const user = useMainStore((s) => s.user);
  const appMode = useMainStore((s) => s.appMode ?? "user");
  if (user?.role === USER_ROLES.ORG_ADMIN && appMode === "user") {
    return <Navigate to={getTargetRoute(user, { appMode: "user" })} replace />;
  }
  return <>{children}</>;
});
OrgAdminAdminModeOnly.displayName = "OrgAdminAdminModeOnly";

/* Home: canonical destination for current user */
const HomeRedirect = () => {
  const user = useMainStore((s) => s.user);
  const appMode = useMainStore((s) => s.appMode ?? "user");
  return <Navigate to={getTargetRoute(user, { appMode })} replace />;
};

/*AuthenticatedContent Component*/
const AuthenticatedContent = () => {
  const [userHasProfile, setUserHasProfile] = useState(null);
  const [isCheckingProfile, setIsCheckingProfile] = useState(true);
  const { setUser, user, appMode } = useMainStore();

  // Check for auth token
  const hasToken = useMemo(() => !!localStorage.getItem("token"), []);
  const didSyncRef = useRef(false);

  // Check user profile status and sync from server
  useEffect(() => {
    let isMounted = true;

    const checkUserProfile = async () => {
      try {
        const token = localStorage.getItem("token");

        if (!token) {
          if (isMounted) setIsCheckingProfile(false);
          return;
        }

        if (token && !user?.id) {
          if (isMounted) {
            setUserHasProfile(false);
            setIsCheckingProfile(false);
          }
          return;
        }

        if (token && user?.id && user?.email) {
          if (isMounted) {
            setUserHasProfile(true);
            setIsCheckingProfile(false);
          }

          if (!didSyncRef.current) {
            didSyncRef.current = true;
            try {
              const fresh = await fetchCurrentUser();
              if (isMounted && fresh?.id) {
                setUser({
                  approvalStatus: fresh.approvalStatus,
                  hasAccessRequest: fresh.hasAccessRequest,
                  isOnboarded: fresh.isOnboarded,
                  role: fresh.role,
                  ...(fresh.organization_llm_type != null &&
                    String(fresh.organization_llm_type).trim() !== "" && {
                    organization_llm_type: fresh.organization_llm_type,
                  }),
                  organization_id:
                    fresh.organization_id ?? fresh.organizationId ?? null,
                  plan: fresh.plan,
                  trial_approved_at: fresh.trial_approved_at,
                  trial_ends_at: fresh.trial_ends_at,
                  ...(fresh.requestTarget != null && {
                    requestTarget: fresh.requestTarget,
                  }),
                  ...(fresh.orgName != null && { orgName: fresh.orgName }),
                  ...(fresh.requestSubmittedAt != null && {
                    requestSubmittedAt: fresh.requestSubmittedAt,
                  }),
                });
              }
            } catch {
              // /auth/me failed -- keep stale store data
            }
          }
          return;
        }

        if (user?.email) {
          if (isMounted) {
            setUserHasProfile(true);
            setIsCheckingProfile(false);
          }
        }
      } catch (err) {
        if (!isMounted) return;
        setUserHasProfile(false);
      } finally {
        if (isMounted) setIsCheckingProfile(false);
      }
    };

    checkUserProfile();

    return () => {
      isMounted = false;
    };
  }, [setUser, user, hasToken]);

  const isLoading = useMemo(() => {
    return isCheckingProfile;
  }, [isCheckingProfile]);

  // Memoize protected route
  const createProtectedRoute = useCallback(
    (Component) => (
      <ProtectedRoute
        userHasProfile={userHasProfile ?? false}
        user={user}
        appMode={appMode ?? "user"}
      >
        <Component />
      </ProtectedRoute>
    ),
    [userHasProfile, user, appMode],
  );

  // Show loading state
  if (isLoading) {
    const loadingText = isCheckingProfile
      ? "Loading User Profile..."
      : undefined;
    return (
      <div className="flex items-center justify-center h-[100dvh] w-full">
        <Loader text={loadingText} />
      </div>
    );
  }

  return (
    <>
      <GlobalEnrollmentBatchTracker />
      <ActiveCallBar />
      <Routes>
        {/* Home route */}
        <Route path={ROUTES.HOME} element={<HomeRedirect user={user} />} />

        {/* Onboarding routes - Redirect to campaigns if already onboarded */}
        <Route element={<LayoutBg background={true} />}>
          <Route
            path={ROUTES.ONBOARDING}
            element={
              <ErrorBoundary>
                <RouteGuard
                  user={user}
                  appMode={appMode ?? "user"}
                  expectedRoute={ROUTES.ONBOARDING}
                >
                  <NewOnboardingNewUser />
                </RouteGuard>
              </ErrorBoundary>
            }
          />
        </Route>
        {/* Main App Layout (with sidebar) - All role-based routes */}
        <Route element={<MainAppLayout />}>
          {/* Super Admin routes */}
          <Route
            path={ROUTES.SA_DASHBOARD}
            element={
              <ErrorBoundary>
                <SuperAdminDashboard />
              </ErrorBoundary>
            }
          />
          <Route
            path={ROUTES.SA_ORGANIZATIONS}
            element={
              <ErrorBoundary>
                <OrganizationsList />
              </ErrorBoundary>
            }
          />
          <Route
            path={ROUTES.SA_CALLING_APPROVALS}
            element={
              <ErrorBoundary>
                <CallingApprovals />
              </ErrorBoundary>
            }
          />
          <Route
            path={`${ROUTES.SA_ORGANIZATIONS}/:orgId`}
            element={
              <ErrorBoundary>
                <OrganizationUsers />
              </ErrorBoundary>
            }
          />
          <Route
            path="/sa/settings/:tab"
            element={
              <ErrorBoundary>
                <Settings />
              </ErrorBoundary>
            }
          />
          {/* Org Admin routes */}
          <Route
            path={ROUTES.OA_DASHBOARD}
            element={
              <ErrorBoundary>
                <OrgAdminAdminModeOnly>
                  <OrgAdminDashboard />
                </OrgAdminAdminModeOnly>
              </ErrorBoundary>
            }
          />
          <Route
            path={ROUTES.OA_MEMBERS}
            element={
              <ErrorBoundary>
                <OrgAdminAdminModeOnly>
                  <MembersList />
                </OrgAdminAdminModeOnly>
              </ErrorBoundary>
            }
          />
          <Route
            path={ROUTES.OA_PENDING_MEMBERS}
            element={
              <ErrorBoundary>
                <OrgAdminAdminModeOnly>
                  <PendingMembers />
                </OrgAdminAdminModeOnly>
              </ErrorBoundary>
            }
          />
          <Route
            path="/oa/settings/:tab"
            element={
              <ErrorBoundary>
                <OrgAdminAdminModeOnly>
                  <Settings />
                </OrgAdminAdminModeOnly>
              </ErrorBoundary>
            }
          />
          <Route
            path={ROUTES.OA_SALES_COACH}
            element={
              <ErrorBoundary>
                <OrgAdminAdminModeOnly>
                  <OrgAdminSalesCoach />
                </OrgAdminAdminModeOnly>
              </ErrorBoundary>
            }
          />
          <Route
            path={`${ROUTES.OA_SALES_COACH}/:tab`}
            element={
              <ErrorBoundary>
                <OrgAdminAdminModeOnly>
                  <OrgAdminSalesCoach />
                </OrgAdminAdminModeOnly>
              </ErrorBoundary>
            }
          />
          <Route
            path={`${ROUTES.OA_SALES_COACH}/:tab/:id`}
            element={
              <ErrorBoundary>
                <OrgAdminAdminModeOnly>
                  <OrgAdminSalesCoach />
                </OrgAdminAdminModeOnly>
              </ErrorBoundary>
            }
          />
          {/* User routes - Protected */}
          <Route
            path={ROUTES.ACCOUNTS}
            element={
              <ErrorBoundary>{createProtectedRoute(Accounts)}</ErrorBoundary>
            }
          />
          <Route
            path={ROUTES.ACCOUNT_DISCOVERY}
            element={
              <ErrorBoundary>{createProtectedRoute(AccountDiscovery)}</ErrorBoundary>
            }
          />
          <Route
            path={`${ROUTES.ACCOUNT_DISCOVERY}/:tab`}
            element={
              <ErrorBoundary>{createProtectedRoute(AccountDiscovery)}</ErrorBoundary>
            }
          />
          <Route
            path={`${ROUTES.ACCOUNTS}/:companyDomain/:tab`}
            element={
              <ErrorBoundary>
                {createProtectedRoute(ProfileDetailedView)}
              </ErrorBoundary>
            }
          />
          <Route
            path={ROUTES.CAMPAIGNS}
            element={
              <ErrorBoundary>{createProtectedRoute(Campaigns)}</ErrorBoundary>
            }
          />
          <Route
            path={`${ROUTES.CAMPAIGNS}/:campaignId/:tab`}
            element={
              <ErrorBoundary>
                {createProtectedRoute(CampaignDetailsPage)}
              </ErrorBoundary>
            }
          />
          <Route
            path={ROUTES.CHAT}
            element={
              <ErrorBoundary>{createProtectedRoute(ChatApp)}</ErrorBoundary>
            }
          />
          <Route
            path={ROUTES.CONTACTS}
            element={
              <ErrorBoundary>{createProtectedRoute(Contacts)}</ErrorBoundary>
            }
          />
          <Route
            path={`${ROUTES.CONTACTS}/:contactId`}
            element={
              <ErrorBoundary>{createProtectedRoute(ContactDetailPage)}</ErrorBoundary>
            }
          />
          <Route
            path={ROUTES.PROSPECTING}
            element={<Navigate to={`${ROUTES.PROSPECTING}/contacts`} replace />}
          />
          {/* One route (optional sub-tab) so moving between /find/accounts and
              /find/accounts/find_look_alikes never remounts the page and loses results. */}
          <Route
            path={`${ROUTES.PROSPECTING}/:tab/:subTab?`}
            element={
              <ErrorBoundary>{createProtectedRoute(Prospect)}</ErrorBoundary>
            }
          />
          {/* Old /prospect links keep working. */}
          <Route
            path={ROUTES.PROSPECT}
            element={<Navigate to={`${ROUTES.PROSPECTING}/contacts`} replace />}
          />
          <Route
            path={`${ROUTES.PROSPECT}/accounts`}
            element={<Navigate to={`${ROUTES.PROSPECTING}/accounts`} replace />}
          />
          <Route
            path={ROUTES.SETTINGS}
            element={
              <Navigate to={`${ROUTES.SETTINGS}/profile_settings`} replace />
            }
          />
          <Route
            path={`${ROUTES.SETTINGS}/:tab`}
            element={
              <ErrorBoundary>{createProtectedRoute(Settings)}</ErrorBoundary>
            }
          />
          <Route
            path={ROUTES.AI_TRAINER}
            element={
              <ErrorBoundary>{createProtectedRoute(AiTrainer)}</ErrorBoundary>
            }
          />
          <Route
            path={ROUTES.SALES_COACH}
            element={
              <ErrorBoundary>{createProtectedRoute(SalesCoach)}</ErrorBoundary>
            }
          />
          <Route
            path={`${ROUTES.SALES_COACH}/learn/:id`}
            element={
              <ErrorBoundary>{createProtectedRoute(SalesCoach)}</ErrorBoundary>
            }
          />
          <Route
            path={ROUTES.DASHBOARD}
            element={
              <ErrorBoundary>
                {createProtectedRoute(DashboardPage)}
              </ErrorBoundary>
            }
          />
          <Route
            path={ROUTES.AI_WORKSPACE}
            element={
              <ErrorBoundary>{createProtectedRoute(AiWorkspace)}</ErrorBoundary>
            }
          />
        </Route>

        {/* Pricing - only after onboarding; first-time / not onboarded → onboarding */}
        <Route
          path={ROUTES.PRICING}
          element={
            <ErrorBoundary>
              <RouteGuard
                user={user}
                appMode={appMode ?? "user"}
                expectedRoute={ROUTES.PRICING}
              >
                <Pricing />
              </RouteGuard>
            </ErrorBoundary>
          }
        />
        {/* Join Organization */}
        <Route
          path={ROUTES.JOIN_ORGANIZATION}
          element={
            <ErrorBoundary>
              <JoinFlowGuard user={user}>
                <JoinOrganization />
              </JoinFlowGuard>
            </ErrorBoundary>
          }
        />
        {/* Admin Request */}
        <Route
          path={ROUTES.ADMIN_REQUEST}
          element={
            <ErrorBoundary>
              <JoinFlowGuard user={user} appMode={appMode ?? "user"}>
                <AdminRequest />
              </JoinFlowGuard>
            </ErrorBoundary>
          }
        />
        {/* Join Organization - Confirm Request */}
        <Route
          path={ROUTES.JOIN_ORGANIZATION_CONFIRM}
          element={
            <ErrorBoundary>
              <JoinFlowGuard user={user} appMode={appMode ?? "user"}>
                <ConfirmationOrgJoin />
              </JoinFlowGuard>
            </ErrorBoundary>
          }
        />
        {/* Join Organization - Request Status */}
        <Route
          path={ROUTES.JOIN_ORGANIZATION_STATUS}
          element={
            <ErrorBoundary>
              <AccessStatusGuard user={user} appMode={appMode ?? "user"}>
                <AccessRequestStatus />
              </AccessStatusGuard>
            </ErrorBoundary>
          }
        />
        <Route
          path={ROUTES.JOIN_ORGANIZATION_REJECTED}
          element={
            <ErrorBoundary>
              <RejectedOnlyGuard user={user} appMode={appMode ?? "user"}>
                <AccessRequestRejected />
              </RejectedOnlyGuard>
            </ErrorBoundary>
          }
        />

        {/* 404 - Not Found */}
        <Route
          path="*"
          element={
            <ErrorBoundary>
              <NotFoundPage />
            </ErrorBoundary>
          }
        />
      </Routes>
    </>
  );
};

export default memo(AuthenticatedContent);
